import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | valid | invalid | done
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/auth/invite/${encodeURIComponent(token)}`, { credentials: "omit" })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        setInvite(await r.json());
        setState("valid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const strong = password.length >= 8 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) return toast.error("Password must be 8+ chars with upper, lower and a number");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/auth/invite/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Could not activate account");
      setState("done");
      toast.success("Account activated");
      setTimeout(() => navigate("/admin", { replace: true }), 1800);
    } catch (err) {
      toast.error(err.message || "Could not activate account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="admin-invite-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Eduzy Admin</h1>
            <p className="text-sm text-slate-500">Set up your account</p>
          </div>
        </div>

        {state === "loading" && (
          <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Checking your invitation…
          </div>
        )}

        {state === "invalid" && (
          <div className="text-center py-8" data-testid="invite-invalid">
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-slate-800 font-medium">This invitation is invalid or has expired.</p>
            <p className="text-sm text-slate-500 mt-1">Please ask a super admin to send a new invite.</p>
            <Button variant="outline" className="mt-5" onClick={() => navigate("/admin")}>Go to login</Button>
          </div>
        )}

        {state === "done" && (
          <div className="text-center py-8" data-testid="invite-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-800 font-medium">Your account is active!</p>
            <p className="text-sm text-slate-500 mt-1">Taking you to the login page…</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            <div className="bg-indigo-50 rounded-lg p-3 text-sm">
              <p className="text-slate-700"><b>{invite?.name}</b></p>
              <p className="text-slate-500">{invite?.email}</p>
              {invite?.role && (
                <p className="text-indigo-700 text-xs mt-1 uppercase tracking-wide">
                  {invite.role.replace(/_/g, " ")}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Create a password</label>
              <div className="relative">
                <Input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="pr-10"
                  data-testid="invite-password-input"
                />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className={`text-xs mt-1 ${strong ? "text-emerald-600" : "text-slate-400"}`}>
                Use upper & lower case letters and a number.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Confirm password</label>
              <Input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                data-testid="invite-confirm-input"
              />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90"
              data-testid="invite-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Activate account
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
